import {useState, useEffect, useCallback, useMemo, useRef} from "react";
import Loading from "../elements/Loading";
import InlineNotification from "../elements/InlineNotification";
import getAPIClient from "../hooks/apiClient";
import '../styles/pages/applycant_analyz.scss';
import { FaSync, FaCheck, FaTimes, FaQuestionCircle } from "react-icons/fa";
import Popup from "../elements/popup";
import VacancySettingsPage from "./VacancySettingsPage";
import {useNavigate, useSearchParams} from "react-router-dom";
import Header from "../elements/Header";
import {CompetencyTable, ConstraintTable, QuestionsTable} from "../elements/AnalysisTables";

export default function ApplicantAnalysPage({ BX24 }) {
    const api = useMemo(() => getAPIClient(BX24), []);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [notificationText, setNotificationText] = useState('');
    const [notificationType, setNotificationType] = useState('error');
    const [crmItemId, setCrmItemId] = useState(null);
    const [applicantData, setApplicantData] = useState({
        status: 'creating',
        score: null,
        summary: null,
        green_flags: [],
        red_flags: [],
        questions: [],
        details: null,
        vacancy_b24_element_id: null
    });

    const timerRef = useRef(null); // Используем useRef вместо useState для таймера

    // // Тестовые данные для демонстрации
    // const demoData = {
    //     status: 'created',
    //     score: 85,
    //     green_flags: [
    //         "Опыт работы 5+ лет в разработке",
    //         "Знание современных фреймворков (React, Vue)",
    //         "Высшее техническое образование",
    //         "Сертификаты по специализации",
    //         "Опыт работы в международных проектах",
    //         "Знание английского языка на уровне Upper-Intermediate"
    //     ],
    //     red_flags: [
    //         "Отсутствие опыта работы в команде",
    //         "Нет портфолио проектов",
    //         "Слабые знания английского языка",
    //         "Отсутствие опыта с тестированием"
    //     ],
    //     questions: [
    //         "Расскажите о вашем опыте работы с микросервисной архитектурой?",
    //         "Как вы организуете работу в команде?",
    //         "Какие методы оптимизации производительности вы применяли?",
    //         "Опишите ваш опыт работы с системами контроля версий?"
    //     ]
    // };
    //
    // // Функция для установки демо-данных
    // useEffect(() => {
    //     setApplicantData(demoData);
    //     setIsLoading(false);
    // }, [])


    const handleResponseError = useCallback((defaultText, e) => {
        console.error('Error:', defaultText, e);
        setNotificationType('error');
        if (e?.response?.data?.detail) {
            setNotificationText(`${e.response.data.detail}`);
            setApplicantData({
                status: 'error',
                score: null,
                green_flags: [],
                red_flags: [],
                questions: [],
                vacancy_b24_element_id: e.response.data.vacancy_b24_element_id || null
            })
        } else {
            setNotificationText(defaultText);
            setApplicantData({
                status: 'error',
                score: null,
                green_flags: [],
                red_flags: [],
                questions: [],
                vacancy_b24_element_id: null
            })
        }
    }, []);

    // Загрузка данных анализа
    const fetchApplicantData = useCallback(async () => {
        if (!crmItemId) return;

        setIsLoading(true);
        try {
            const response = await api.get(`/analysis_agent/applicant?crm_item_id=${crmItemId}`);
            if (response.data) {
                setApplicantData({
                    status: response.data.status || 'creating',
                    score: response.data.score || 0,
                    summary: response.data.summary || null,
                    green_flags: response.data.green_flags || [],
                    red_flags: response.data.red_flags || [],
                    questions: response.data.questions || [],
                    details: response.data.details || null,
                    vacancy_b24_element_id: response.data.vacancy_b24_element_id || null
                });

                // Если статус creating, запускаем таймер для обновления
                if (response.data.status === 'creating' || response.data.status === 'updating') {
                    // Очищаем предыдущий таймер
                    if (timerRef.current) clearTimeout(timerRef.current);

                    // Устанавливаем новый таймер
                    timerRef.current = setTimeout(() => {
                        fetchApplicantData();
                    }, 5000);
                }
            }
        } catch (e) {
            handleResponseError('Ошибка загрузки данных анализа', e);
        } finally {
            setIsLoading(false);
            BX24.fitWindow()
        }
    }, [api, crmItemId, handleResponseError]);

    // Обновление анализа
    const refreshAnalysis = useCallback(async () => {
        setIsLoading(true);
        try {
            await api.get(`/analysis_agent/applicant?crm_item_id=${crmItemId}&update=true`);

            // Используем функциональное обновление состояния
            setApplicantData(prev => ({
                ...prev,
                status: 'updating'
            }));

            // Очищаем предыдущий таймер
            if (timerRef.current) clearTimeout(timerRef.current);

            // Устанавливаем новый таймер
            timerRef.current = setTimeout(() => {
                fetchApplicantData();
            }, 3000);

            setNotificationType('info');
            setNotificationText('Анализ обновляется...');
        } catch (e) {
            handleResponseError('Ошибка обновления анализа', e);
        } finally {
            setIsLoading(false);
            BX24.fitWindow()
        }
    }, [api, crmItemId, applicantData, fetchApplicantData, handleResponseError]);

    // Инициализация
    useEffect(() => {
        const init = async () => {
            try {
                BX24.fitWindow()
                const fromQuery = searchParams.get('crm_item_id');
                if (fromQuery && String(fromQuery).match(/^\d+$/)) {
                    setCrmItemId(fromQuery);
                    return;
                }
                const placement = await BX24.placement.info();
                const itemId = placement?.options?.ID || placement?.options?.ENTITY_ID;
                if (itemId) {
                    setCrmItemId(itemId);
                } else {
                    setNotificationType('error');
                    setNotificationText('Не удалось получить ID соискателя');
                    setIsLoading(false);
                }
            } catch (e) {
                handleResponseError('Ошибка инициализации', e);
                setIsLoading(false);
            }
        };
        init();

        // Очистка таймера при размонтировании
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [BX24, handleResponseError, searchParams]);

    // При изменении ID соискателя загружаем данные
    useEffect(() => {
        if (crmItemId) {
            fetchApplicantData();
        }
    }, [crmItemId, fetchApplicantData]);

    // Подгоняем высоту iframe Битрикса под фактический контент
    // после рендера (не в finally, иначе fitWindow срабатывает до появления таблиц).
    useEffect(() => {
        const id = requestAnimationFrame(() => {
            try { BX24.fitWindow(); } catch (e) {}
        });
        return () => cancelAnimationFrame(id);
    }, [BX24, isLoading, applicantData]);

    // Отрисовка индикатора оценки
    const renderScoreIndicator = useCallback(() => {
        if (applicantData.status !== 'created' || applicantData.score === null) {
            return null;
        }

        const getColor = (score) => {
            if (score >= 8) return '#52c41a';
            if (score >= 6) return '#faad14';
            return '#ff4d4f';
        };

        const color = getColor(applicantData.score);

        return (
          <div className="score-indicator">
              <div className="score-circle" style={{
                  background: `conic-gradient(${color} ${applicantData.score * 36}deg, #f0f0f0 0deg)`
              }}>
                  <div className="score-value">{applicantData.score}<span style={{fontSize: "20px"}}>/10</span></div>
              </div>
              <div className="score-label">Оценка соответствия</div>
          </div>

        );
    }, [applicantData]);

    // Отрисовка флагов
    const renderFlags = useCallback((flags, type) => {
        if (!flags || flags.length === 0) return null;

        return (
          <div className={`flags-section ${type}-flags`}>
              <h3>
                  {type === 'green' ?
                    <><FaCheck className="icon green" /> Зеленые флаги</> :
                    <><FaTimes className="icon red" /> Красные флаги</>
                  }
                  <span className="count-badge">{flags.length}</span>
              </h3>
              <div className="flags-grid">
                  {flags.map((flag, index) => (
                    <div key={index} className="flag-card">
                        {flag}
                    </div>
                  ))}
              </div>
          </div>
        );
    }, []);

    // Отрисовка вопросов
    const renderQuestions = useCallback(() => {
        if (!applicantData.questions || applicantData.questions.length === 0) return null;

        return (
          <div className="questions-section">
              <h3><FaQuestionCircle className="icon blue" /> Вопросы для уточнения</h3>
              <div className="questions-list">
                  {applicantData.questions.map((question, index) => (
                    <div key={index} className="question-item">
                        <div className="question-number">{index + 1}</div>
                        <div className="question-text">{question}</div>
                    </div>
                  ))}
              </div>
          </div>
        );
    }, [applicantData]);

    // Отображение статуса
    const renderStatus = useCallback(() => {
        let statusText, statusClass;

        switch (applicantData.status) {
            case 'creating':
                statusText = 'Анализ выполняется...';
                statusClass = 'info';
                break;
            case 'updating':
                statusText = 'Анализ обновляется...';
                statusClass = 'info';
                break;
            case 'created':
                statusText = 'Анализ завершен';
                statusClass = 'success';
                break;
            case 'error':
                statusText = 'Ошибка анализа';
                statusClass = 'error';
                break;
            default:
                statusText = 'Статус неизвестен';
                statusClass = 'warning';
        }

        return (
          <div className={`status-badge ${statusClass}`}>
              {statusText}
              {applicantData.status === 'creating' && (
                <div className="loading-dots">
                    <span>.</span><span>.</span><span>.</span>
                </div>
              )}
          </div>
        );
    }, [applicantData]);

    const [vacancyPopupSettingsOpened, setVacancyPopupSettingsOpened] = useState(false)

    const [activeTab, setActiveTab] = useState(0);
    const [tabKey, setTabKey] = useState(0);
    const [slideDir, setSlideDir] = useState('forward');

    const changeTab = useCallback((idx) => {
        if (idx === activeTab) return;
        setSlideDir(idx > activeTab ? 'forward' : 'back');
        setTabKey(k => k + 1);
        setActiveTab(idx);
    }, [activeTab]);

    const canReturnToDashboard = searchParams.get('return_to') === 'resume_dashboard' || searchParams.get('return_to') === 'resume_workspace';
    const returnVacancyId = searchParams.get('vacancy_id') || '';
    const returnIds = searchParams.get('ids') || '';
    const returnDirectionId = searchParams.get('direction_id') || '';
    const returnStageIds = searchParams.get('stage_ids') || '';
    const buildReturnUrl = () => {
        const params = new URLSearchParams();
        if (returnVacancyId) params.set('vacancy_id', returnVacancyId);
        if (returnDirectionId) params.set('direction_id', returnDirectionId);
        if (returnStageIds) params.set('stage_ids', returnStageIds);
        const qs = params.toString();
        return `/resume_workspace${qs ? `?${qs}` : ''}`;
    };

    return (
      <div className={''}>
          <Header/>
          <div className={'main-block applicant-analysis'}>
              <InlineNotification
                type={notificationType}
                text={notificationText}
                hideFlag={notificationText.length === 0}
              />

              {isLoading ? (
                <div className="spinner"></div>
              ) : (
                <div className="analysis-container">
                    <div className="analysis-header">
                        <h2>Анализ соискателя</h2>
                        <div className="header-actions">
                            {canReturnToDashboard && (
                                <button
                                    className="btn-vacancy"
                                    onClick={() => navigate(buildReturnUrl())}
                                >
                                    Назад в дашборд
                                </button>
                            )}
                            <button
                                className="btn-vacancy"
                                onClick={() => setVacancyPopupSettingsOpened(true)}
                                disabled={!applicantData.vacancy_b24_element_id}
                            >
                                Настройка вакансии
                            </button>
                            <button
                              className="btn-refresh"
                              onClick={refreshAnalysis}
                              disabled={applicantData.status === 'creating' || applicantData.status === 'updating'}
                            >
                                <FaSync /> Обновить анализ
                            </button>
                            {renderStatus()}
                        </div>
                    </div>

                    {applicantData.status === 'error' ? (
                      <div className="error-message">
                          <p>Произошла ошибка при анализе соискателя.</p>
                          <p>Попробуйте обновить анализ или обратитесь в поддержку.</p>
                          <button
                            className="btn-retry"
                            onClick={refreshAnalysis}
                          >
                              <FaSync /> Попробовать снова
                          </button>
                      </div>
                    ) : applicantData.status !== 'created' ? (
                      <div className="creating-state">
                          <div className="spinner"></div>
                          <p>Анализ вашего соискателя выполняется...</p>
                          <p>Это может занять несколько минут.</p>
                      </div>
                    ) : (
                      <div className="analysis-content">
                          {applicantData.score !== null && (
                              <div className="score-sidebar">
                                  {renderScoreIndicator()}
                              </div>
                          )}
                          <div className="analysis-tabs">
                              <div className="tabs-nav">
                                  {[
                                      {
                                          label: applicantData.details ? 'Компетенции' : 'Сильные стороны',
                                          count: applicantData.details?.competency_scores?.length ?? applicantData.green_flags?.length ?? 0,
                                      },
                                      {
                                          label: applicantData.details ? 'Ограничения' : 'Слабые стороны',
                                          count: applicantData.details?.constraint_checks?.length ?? applicantData.red_flags?.length ?? 0,
                                      },
                                      {
                                          label: 'Вопросы',
                                          count: applicantData.questions?.length ?? 0,
                                      },
                                  ].map((tab, i) => (
                                      <button
                                          key={i}
                                          className={`tab-btn${activeTab === i ? ' active' : ''}`}
                                          onClick={() => changeTab(i)}
                                      >
                                          {tab.label}
                                          {tab.count > 0 && <span className="tab-badge">{tab.count}</span>}
                                      </button>
                                  ))}
                              </div>

                              <div key={tabKey} className={`tab-panel-anim ${slideDir}`}>
                                  {activeTab === 0 && (applicantData.details
                                      ? <CompetencyTable items={applicantData.details.competency_scores} />
                                      : <div className="flags-container">{renderFlags(applicantData.green_flags, 'green')}</div>
                                  )}
                                  {activeTab === 1 && (applicantData.details
                                      ? <ConstraintTable items={applicantData.details.constraint_checks} />
                                      : <div className="flags-container">{renderFlags(applicantData.red_flags, 'red')}</div>
                                  )}
                                  {activeTab === 2 && (
                                      applicantData.details
                                          ? <QuestionsTable items={applicantData.questions} />
                                          : (renderQuestions() || <p className="empty-tab">Вопросов для уточнения нет</p>)
                                  )}
                              </div>
                          </div>
                      </div>
                    )}
                </div>
              )}
          </div>
          <Popup openedFlag={vacancyPopupSettingsOpened}
                 setOpenedFlag={setVacancyPopupSettingsOpened}>
              {(applicantData.vacancy_b24_element_id) && <VacancySettingsPage BX24={BX24} vacancyItemId={applicantData.vacancy_b24_element_id}/>}
          </Popup>
      </div>
    );
}
